import { useEffect, useMemo, useState } from "react";
import type { DeviceType } from "../types";

export const BREAKPOINTS = {
  s: 480,
  m: 768,
  l: 1024,
  xl: 1280,
  xxl: 1440,
} as const;

function makeQuery(min?: number, max?: number) {
  if (min != null && max != null)
    return `(min-width:${min}px) and (max-width:${max - 1}px)`;
  if (min != null) return `(min-width:${min}px)`;
  if (max != null) return `(max-width:${max - 1}px)`;
  return "";
}

function onMQChange(
  mql: MediaQueryList,
  handler: (e: MediaQueryListEvent) => void,
) {
  mql.addEventListener("change", handler);
  return () => mql.removeEventListener("change", handler);
}

export function useMediaQuery(query: string) {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === "undefined" || !query) return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !query) return null;
    const mql = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
// eslint-disable-next-line react-hooks/exhaustive-deps
    setMatches(mql.matches)
    const cleanup = onMQChange(mql, handler);
    return cleanup;
  }, [query]);

  return matches;
}

export function useDeviceType() {
  const isMobile = useMediaQuery(makeQuery(undefined, BREAKPOINTS.m));
  const isTablet = useMediaQuery(makeQuery(BREAKPOINTS.m, BREAKPOINTS.l));
  const isDesktopOnly = useMediaQuery(
    makeQuery(BREAKPOINTS.l, BREAKPOINTS.xxl),
  );
  const isWide = useMediaQuery(makeQuery(BREAKPOINTS.xxl));
  const isDesktop = isDesktopOnly || isWide;

  const [width, setWidth] = useState<number>(
    typeof window !== "undefined" ? window.innerWidth : BREAKPOINTS.l,
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", onResize, { passive: true });
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const device: DeviceType = useMemo(() => {
    const w = width;
    if (w < BREAKPOINTS.m) return "mobile";
    if (w < BREAKPOINTS.l) return "tablet";
    if (w < BREAKPOINTS.xxl) return "desktop";
    return "wide"; // 1440+ (XXL)
  }, [width]);

  return { device, width, isMobile, isTablet, isDesktop, isWide };
}
